import lookups from "../api/lookups";
import { AdvancedSearchNewOptionType } from "../config/enums";
import { EntityFieldCatalogEntry } from "../builder/entityFieldCatalog.types";
import { SearchColumn } from "./advancedSearch.types";
import { getCachedCatalog } from "../builder/entityCatalogCache";
import { getSearchEntityConfig } from "./searchEntities";
import { SearchFieldConfig, SearchJoin } from "./searchEntities.types";

type Filters = { Key: string; Value: string }[];
type Enum = Record<string, number>;

const DateFormat = "dd/MM/yyyy";
const DateTimeFormat = "dd/MM/yyyy HH:mm:ss";

const StatusName = "statecode";

// The status the service doesn't describe usefully: a state set with Active and Inactive as
// its words, saved as the number the service expects.
const StatusField: SearchColumn = {
  name: StatusName,
  label: "Status",
  allowBulkEdit: false,
  allowFilter: true,
  convertToInt: true,
  defaultValue: true,
  falseLabel: "Inactive",
  inverted: true,
  trueLabel: "Active",
  type: "bit",
};

const lookupOf = (entityName: string | undefined) =>
  (entityName && (lookups as Record<string, { all?: unknown }>)[entityName]?.all) || null;

/** The words an option set offers, as the legacy descriptors hold them: label to value. */
function toEnum(entry: EntityFieldCatalogEntry): Enum {
  return (entry.options ?? []).reduce<Enum>((list, option) => {
    list[option.Value] = Number(option.Key);

    return list;
  }, {});
}

function describeCurated(
  entry: EntityFieldCatalogEntry,
  config: SearchFieldConfig
): SearchColumn | null {
  const base = {
    name: entry.name,
    label: config.label,
    allowBulkEdit: Boolean(config.bulkEdit),
    allowFilter: config.filter !== false,
  };

  switch (entry.fieldType) {
    case "text":
    case "longtext":
      return { ...base, type: "text" };
    case "number":
      return { ...base, type: "int" };
    case "money":
      return { ...base, type: "money" };
    case "datetime":
      return config.dateTime
        ? { ...base, type: "datetime", dateFormat: DateTimeFormat, convertToLocalTime: true }
        : { ...base, type: "datetime", dateFormat: DateFormat, convertToLocalTime: false };
    case "boolean":
      return config.inverted
        ? { ...base, type: "bit", inverted: true, defaultValue: true }
        : { ...base, type: "bit", defaultValue: false };
    case "picklist": {
      const list = toEnum(entry);
      const unknown = config.unknownLabel;
      const display = unknown
        ? (value: unknown) =>
            Object.keys(list).find((key) => String(list[key]) === String(value)) ?? unknown
        : undefined;

      return { ...base, type: "picklist", enum: list, ...(display ? { displayFieldValue: display } : {}) };
    }
    case "lookup":
      return {
        ...base,
        name: `${entry.name}name`,
        type: "select",
        linkedEntityId: entry.name,
        linkForNewRecord: config.link ?? null,
        lookup: lookupOf(entry.lookupEntity),
      };
    default:
      return null;
  }
}

/** Attributes outside the default list: filterable, never bulk edited, and opt in. */
function describeDiscovered(entry: EntityFieldCatalogEntry): SearchColumn | null {
  const base = { name: entry.name, label: entry.label, allowBulkEdit: false, allowFilter: true };

  switch (entry.fieldType) {
    case "text":
    case "longtext":
      return { ...base, type: "text", isMetadataField: true };
    case "number":
      return { ...base, type: "int", isMetadataField: true };
    case "money":
      return { ...base, type: "money", isMetadataField: true };
    case "datetime":
      return { ...base, type: "datetime", dateFormat: DateFormat, convertToLocalTime: false, isMetadataField: true };
    case "boolean":
      return { ...base, type: "bit", defaultValue: false, isMetadataField: true };
    case "picklist":
      return { ...base, type: "picklist", enum: toEnum(entry), isMetadataField: true };
    default:
      return null;
  }
}

function describeFields(entityName: string, catalog: EntityFieldCatalogEntry[]): SearchColumn[] {
  const config = getSearchEntityConfig(entityName);
  const byName = new Map(catalog.map((entry) => [entry.name, entry]));
  const curatedNames = new Set((config?.fields ?? []).map((field) => field.name));
  const fields: SearchColumn[] = [];

  (config?.fields ?? []).forEach((field) => {
    if (field.name === StatusName) {
      fields.push(StatusField);

      return;
    }

    const entry = byName.get(field.name);
    const described = entry && describeCurated(entry, field);

    if (described) {
      fields.push(described);
    }
  });

  catalog.forEach((entry) => {
    const described = entry.kind === "field" && !curatedNames.has(entry.name) && describeDiscovered(entry);

    if (described) {
      fields.push(described);
    }
  });

  return fields;
}

// One empty list, so "nothing loaded yet" compares equal from one call to the next.
const NoCatalog: EntityFieldCatalogEntry[] = [];

const resolved = new Map<string, { catalog: EntityFieldCatalogEntry[]; fields: SearchColumn[] }>();

/**
 * The fields searching an entity offers. The default list comes first, in its own order.
 * Empty of anything metadata describes until `loadSearchCatalog` has resolved for the entity.
 */
export function getAvailableFieldsFromSearchEntity(entityName: string | undefined): SearchColumn[] {
  if (!entityName) {
    return [];
  }

  const catalog = getCachedCatalog(entityName.toLowerCase()) ?? NoCatalog;
  const known = resolved.get(entityName);

  if (known && known.catalog === catalog) {
    return known.fields;
  }

  const fields = describeFields(entityName, catalog);

  // Status needs no metadata, so it is offered before the catalog arrives and never cached.
  if (catalog.length > 0) {
    resolved.set(entityName, { catalog, fields });
  }

  return fields;
}

export function getAdvancedSearchEntityName(filters: Filters) {
  return filters.find((filter) => filter.Key === "advancedSearchEntityName")?.Value ?? "";
}

/** The fields the entity starts with as columns, in the order they are listed. */
export function getDefaultFieldsFromSearchEntity(entityName: string): SearchColumn[] {
  const config = getSearchEntityConfig(entityName);
  const available = getAvailableFieldsFromSearchEntity(entityName);

  return (config?.defaultColumns ?? [])
    .map((name) => available.find((field) => field.linkedEntityId === name || field.name === name))
    .filter((field): field is SearchColumn => Boolean(field));
}

export function getBulkEditFieldOptionsFromSearchEntity(entityName: string) {
  return getAvailableFieldsFromSearchEntity(entityName)
    .filter((field) => field.allowBulkEdit)
    .map((field) => ({
      Key: field.linkedEntityId ? field.linkedEntityId : field.name,
      Value: field.label,
    }))
    .sort((a, b) => a.Value.localeCompare(b.Value));
}

export const getLabelForJoinEntity = (join: SearchJoin | undefined, fieldLabel: string) =>
  join ? `${join.Label}: ${fieldLabel}` : fieldLabel;

function splitJoinedName(filters: Filters, joins: SearchJoin[] | undefined, value: string) {
  if (!value.includes(".")) {
    return { fieldName: value, entityName: getAdvancedSearchEntityName(filters), join: undefined };
  }

  const [alias, fieldName] = value.split(".", 2);
  const join = joins?.find((candidate) => candidate.Alias === alias);

  return { fieldName, entityName: join?.EntityName, join };
}

export function getField(filters: Filters, joins: SearchJoin[] | undefined, field: string): {
  join?: SearchJoin;
  entityName?: string;
  fieldName: string;
  field: SearchColumn;
} | null {
  const { fieldName, entityName, join } = splitJoinedName(filters, joins, field);
  const available = getAvailableFieldsFromSearchEntity(entityName);
  const matched = available.find((candidate) => candidate.name === fieldName);

  if (matched) {
    return { join, entityName, fieldName, field: matched };
  }

  const lookup = available.find((candidate) => candidate.name === `${fieldName}name`);

  return lookup ? getField(filters, joins, join ? `${join.Alias}.${lookup.name}` : lookup.name) : null;
}

export function getFieldFromName(
  filters: Filters,
  joins: SearchJoin[] | undefined,
  value: string
): SearchColumn | null {
  const { fieldName, entityName, join } = splitJoinedName(filters, joins, value);

  if (!entityName) {
    console.warn("Entity not found");

    return null;
  }

  const field = getAvailableFieldsFromSearchEntity(entityName).find((candidate) => candidate.name === fieldName);

  if (!field) {
    return null;
  }

  return {
    ...field,
    label: join ? `${join.Label}: ${field.label}` : field.label,
    name: join ? `${join.Alias}.${field.name}` : field.name,
  };
}

function getColumnOptions(entityName: string, type: string, includeAllFields: boolean) {
  return getAvailableFieldsFromSearchEntity(entityName)
    .filter((field) => {
      // Fields outside the default list are opt in. This only narrows what the pickers
      // offer; getField still resolves them.
      if (field.isMetadataField && !includeAllFields) {
        return false;
      }

      return type !== AdvancedSearchNewOptionType.Filters || field.allowFilter;
    })
    .map((field) => ({ Key: field.name, Value: field.label }))
    .sort((a, b) => a.Value.localeCompare(b.Value));
}

/** The fields a new column or filter can be added on, joined entities included. */
export function getNewColumnOptions(
  filters: Filters,
  joins: SearchJoin[] | undefined,
  tableFields: { name: string }[],
  type: string,
  includeAllFields = false
) {
  const options = getColumnOptions(getAdvancedSearchEntityName(filters), type, includeAllFields);

  if (options.length === 0) {
    return options;
  }

  const related = (joins ?? [])
    .filter(
      (join) =>
        type === AdvancedSearchNewOptionType.Filters ||
        (type === AdvancedSearchNewOptionType.Columns && join.AllowColumns)
    )
    .flatMap((join) =>
      getColumnOptions(join.EntityName, type, includeAllFields).map((option) => ({
        Key: `${join.Alias}.${option.Key}`,
        Value: getLabelForJoinEntity(join, option.Value),
      }))
    );

  const excluded = new Set(tableFields.map((field) => field.name));

  return [...options, ...related]
    .filter((option) => !excluded.has(option.Key))
    .sort((a, b) => a.Value.localeCompare(b.Value));
}
